import { useEffect, useState } from "react";
import { api, fmtTime, SITE_HOST, type Customer, type OtpSession, type Webapp } from "../api";

export function WebappsTab() {
  const [items, setItems] = useState<Webapp[] | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Webapp | null>(null);
  const [secrets, setSecrets] = useState<{
    code: string;
    api_key: string;
    hmac_secret: string;
    webhook_url: string;
  } | null>(null);
  const [openSessions, setOpenSessions] = useState<Webapp | null>(null);

  async function reload() {
    try {
      const r = await api.listWebapps();
      setItems(r.items);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "load_failed");
    }
  }
  useEffect(() => {
    reload();
    api
      .listCustomers()
      .then((r) => setCustomers(r.items))
      .catch(() => setCustomers([]));
  }, []);

  const ownerEmail = (id: string | null) =>
    id ? customers.find((cu) => cu.id === id)?.email ?? id : null;

  async function toggleActive(w: Webapp) {
    await api.updateWebapp(w.id, { active: !w.active });
    reload();
  }
  async function remove(w: Webapp) {
    if (!confirm(`Delete webapp ${w.name} (${w.code})? This cannot be undone.`)) return;
    await api.deleteWebapp(w.id);
    reload();
  }
  async function rotateKey(w: Webapp) {
    if (!confirm(`Rotate API key for ${w.name}? The old key stops working.`)) return;
    const r = await api.rotateApiKey(w.id);
    setSecrets({ code: w.code, api_key: r.api_key, hmac_secret: "", webhook_url: "" });
  }
  async function rotateHmac(w: Webapp) {
    if (!confirm(`Rotate HMAC secret for ${w.name}? Outbound callbacks must verify with the new secret.`)) return;
    const r = await api.rotateHmac(w.id);
    setSecrets({ code: w.code, api_key: "", hmac_secret: r.hmac_secret, webhook_url: "" });
  }
  async function rotateWebhookKey(w: Webapp) {
    if (!confirm(`Rotate inbound webhook URL for ${w.name}? Update your SMS gateway to the new URL.`)) return;
    const r = await api.rotateWebhookKey(w.id);
    setSecrets({ code: w.code, api_key: "", hmac_secret: "", webhook_url: r.webhook_url });
    reload();
  }

  return (
    <>
      <div className="panel">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2>Webapps</h2>
          <div style={{ flex: "0 0 auto" }}>
            <button onClick={() => setShowCreate(true)}>New webapp</button>
          </div>
        </div>
        {err && <div className="error">{err}</div>}
        {items === null ? (
          <div className="muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="muted">No webapps yet.</div>
        ) : (
          <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Owner</th>
                <th>Callback URL</th>
                <th>Inbound #</th>
                <th>Inbound webhook</th>
                <th>Status</th>
                <th>Usage</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((w) => (
                <tr key={w.id}>
                  <td data-label="Code">
                    {w.code_hidden ? (
                      <span className="muted">removed</span>
                    ) : (
                      <code style={{ whiteSpace: "nowrap" }}>{w.code}</code>
                    )}
                  </td>
                  <td data-label="Name">{w.name}</td>
                  <td data-label="Owner">
                    {ownerEmail(w.owner_customer_id) ?? <span className="muted">operator</span>}
                  </td>
                  <td data-label="Callback URL"><code>{w.callback_url}</code></td>
                  <td data-label="Inbound #">
                    {w.inbound_number ? (
                      <code>{w.inbound_number}</code>
                    ) : (
                      <span className="muted">shared</span>
                    )}
                  </td>
                  <td data-label="Inbound webhook">
                    {w.has_webhook_key ? (
                      <span className="pill ok">provisioned</span>
                    ) : (
                      <span className="muted">none</span>
                    )}
                  </td>
                  <td data-label="Status">
                    <span className={`pill ${w.active ? "ok" : "bad"}`}>
                      {w.active ? "active" : "disabled"}
                    </span>
                    {w.owner_suspended && (
                      <>
                        <br />
                        <span
                          className="pill bad"
                          title="The owning customer account is suspended; the API refuses this webapp. Lift it from the Customers tab."
                        >
                          owner suspended
                        </span>
                      </>
                    )}
                  </td>
                  <td data-label="Usage">
                    <span title={billingLabel(w)}>
                      {w.unlimited ? (
                        <code>{w.verified_count} / ∞</code>
                      ) : w.quota_source === "account" ? (
                        <>
                          <code>{w.verified_count}</code>{" "}
                          <span className="muted">
                            · account pool {w.quota_remaining} / {w.quota_allowance} left
                          </span>
                        </>
                      ) : (
                        <code>
                          {w.verified_count} / {w.quota_allowance}
                        </code>
                      )}
                    </span>
                    <br />
                    <span className={`pill ${billingPill(w.billing_status)}`}>
                      {billingLabel(w)}
                    </span>
                  </td>
                  <td data-label="Created">{fmtTime(w.created_at)}</td>
                  <td className="actions" data-label="Actions">
                    <div className="action-group">
                      <button className="secondary" onClick={() => setOpenSessions(w)}>Sessions</button>
                      <button className="secondary" onClick={() => setEditing(w)}>Edit</button>
                      <button className="secondary" onClick={() => toggleActive(w)}>
                        {w.active ? "Disable" : "Enable"}
                      </button>
                      <button className="secondary" onClick={() => rotateKey(w)}>Rotate key</button>
                      <button className="secondary" onClick={() => rotateHmac(w)}>Rotate HMAC</button>
                      <button className="secondary" onClick={() => rotateWebhookKey(w)}>
                        {w.has_webhook_key ? "Rotate webhook" : "Issue webhook"}
                      </button>
                      <button className="danger" onClick={() => remove(w)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateWebappModal
          onCancel={() => setShowCreate(false)}
          onCreated={(s) => {
            setShowCreate(false);
            setSecrets(s);
            reload();
          }}
        />
      )}


      {secrets && (
        <SecretsModal data={secrets} onClose={() => setSecrets(null)} />
      )}

      {openSessions && (
        <SessionsModal webapp={openSessions} onClose={() => setOpenSessions(null)} />
      )}

      {editing && (
        <EditWebappModal
          webapp={editing}
          customers={customers}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </>
  );
}

function CreateWebappModal({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (s: { code: string; api_key: string; hmac_secret: string; webhook_url: string }) => void;
}) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [callback, setCallback] = useState("https://");
  const [inbound, setInbound] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api.createWebapp({
        name,
        callback_url: callback,
        code: code || undefined,
        inbound_number: inbound.trim() || null,
      });
      onCreated({
        code: r.code,
        api_key: r.api_key,
        hmac_secret: r.hmac_secret,
        webhook_url: r.webhook_url,
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "create_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>New webapp</h3>
        <label>Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} required />
        <label>4-letter code (leave blank to auto-generate)</label>
        <input
          value={code}
          maxLength={4}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))}
          placeholder="ABCD"
        />
        <label>Callback URL</label>
        <input
          type="url"
          value={callback}
          onChange={(e) => setCallback(e.target.value)}
          required
        />
        <label>
          Dedicated inbound number{" "}
          <span className="muted">(optional — users text only the 6-digit OTP to this number)</span>
        </label>
        <input
          value={inbound}
          onChange={(e) => setInbound(e.target.value)}
          placeholder="+90850XXXXXXX"
        />
        {err && <div className="error">{err}</div>}
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <div style={{ flex: "0 0 auto" }}>
            <button type="button" className="secondary" onClick={onCancel}>Cancel</button>{" "}
            <button type="submit" disabled={busy}>
              {busy ? "Creating…" : "Create"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function EditWebappModal({
  webapp,
  customers,
  onCancel,
  onSaved,
}: {
  webapp: Webapp;
  customers: Customer[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(webapp.name);
  const [callback, setCallback] = useState(webapp.callback_url);
  const [inbound, setInbound] = useState(webapp.inbound_number ?? "");
  const [removeCode, setRemoveCode] = useState(webapp.code_hidden);
  const [owner, setOwner] = useState(webapp.owner_customer_id ?? "");
  const [freeQuota, setFreeQuota] = useState(String(webapp.free_quota));
  const [paidQuota, setPaidQuota] = useState(String(webapp.paid_quota));
  const [unlimited, setUnlimited] = useState(webapp.unlimited);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // The code can only be removed in dedicated mode (a dedicated inbound number
  // is what lets us route an OTP-only SMS without the prefix).
  const hasInbound = inbound.trim().length > 0;
  const codeRemovable = hasInbound;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.updateWebapp(webapp.id, {
        name,
        callback_url: callback,
        inbound_number: inbound.trim() ? inbound.trim() : null,
        // Removing the code requires a dedicated number; force it off otherwise.
        code_hidden: codeRemovable ? removeCode : false,
        owner_customer_id: owner ? owner : null,
        free_quota: Number(freeQuota) || 0,
        paid_quota: Number(paidQuota) || 0,
        unlimited,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "update_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Edit {webapp.code_hidden ? webapp.name : webapp.code}</h3>
        <label>Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} required />
        <label>Owner</label>
        <select value={owner} onChange={(e) => setOwner(e.target.value)}>
          <option value="">Unassigned (operator)</option>
          {customers.map((cu) => (
            <option key={cu.id} value={cu.id}>
              {cu.email}
            </option>
          ))}
        </select>
        <label>Callback URL</label>
        <input
          type="url"
          value={callback}
          onChange={(e) => setCallback(e.target.value)}
          required
        />
        <label>
          Dedicated inbound number{" "}
          <span className="muted">(blank = shared, code-prefix mode)</span>
        </label>
        <input
          value={inbound}
          onChange={(e) => setInbound(e.target.value)}
          placeholder="+90850XXXXXXX"
        />
        <label className="row" style={{ gap: 8, alignItems: "center", marginTop: 8 }}>
          <input
            type="checkbox"
            checked={codeRemovable && removeCode}
            disabled={!codeRemovable}
            onChange={(e) => setRemoveCode(e.target.checked)}
            style={{ width: "auto" }}
          />
          <span>
            Remove 4-letter code{" "}
            <span className="muted">
              {codeRemovable
                ? "(user texts only the OTP to the dedicated number)"
                : "(requires a dedicated inbound number)"}
            </span>
          </span>
        </label>
        <label>
          Billing{" "}
          <span className="muted">
            (used {webapp.verified_count} verified so far)
          </span>
        </label>
        <label className="row" style={{ gap: 8, alignItems: "center" }}>
          <input
            type="checkbox"
            checked={unlimited}
            onChange={(e) => setUnlimited(e.target.checked)}
            style={{ width: "auto" }}
          />
          <span>Unlimited (no quota enforcement)</span>
        </label>
        {!unlimited && owner && (
          <p className="muted">
            Customer-owned: verifications are metered against the customer's shared credit pool
            (Customers tab). Free / paid quota below are not used while an owner is set.
          </p>
        )}
        {!unlimited && (
          <>
            <label>
              Free trial quota{" "}
              <span className="muted">(lifetime free verifications)</span>
            </label>
            <input
              type="number"
              min={0}
              value={freeQuota}
              onChange={(e) => setFreeQuota(e.target.value)}
            />
            <label>
              Paid quota{" "}
              <span className="muted">(extra allowance from subscription / top-up)</span>
            </label>
            <input
              type="number"
              min={0}
              value={paidQuota}
              onChange={(e) => setPaidQuota(e.target.value)}
            />
          </>
        )}
        {err && <div className="error">{err}</div>}
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <div style={{ flex: "0 0 auto" }}>
            <button type="button" className="secondary" onClick={onCancel}>Cancel</button>{" "}
            <button type="submit" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function downloadSecrets(data: { code: string; api_key: string; hmac_secret: string; webhook_url: string }) {
  const lines = [
    `# ${SITE_HOST} — webapp keys`,
    "# Keep these secret; do not commit them to source control.",
    `# Downloaded: ${new Date().toISOString()}`,
    "",
    `WEBAPP_CODE=${data.code}`,
  ];
  if (data.api_key) lines.push(`API_KEY=${data.api_key}`);
  if (data.hmac_secret) lines.push(`HMAC_SECRET=${data.hmac_secret}`);
  if (data.webhook_url) lines.push(`WEBHOOK_URL=${data.webhook_url}`);
  const blob = new Blob([lines.join("\n") + "\n"], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `mioe-login-keys-${data.code}.env`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function SecretsModal({
  data,
  onClose,
}: {
  data: { code: string; api_key: string; hmac_secret: string; webhook_url: string };
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Save these now — shown only once</h3>
        <div className="muted" style={{ marginBottom: 8 }}>
          These values are shown only once. Download them or store them somewhere safe.
        </div>
        <p className="muted">Code: <code>{data.code}</code></p>
        {data.api_key && (
          <>
            <label>API key (X-API-Key header)</label>
            <pre>{data.api_key}</pre>
          </>
        )}
        {data.hmac_secret && (
          <>
            <label>HMAC secret (verify X-Login-Signature on callbacks)</label>
            <pre>{data.hmac_secret}</pre>
          </>
        )}
        {data.webhook_url && (
          <>
            <label>
              Inbound webhook URL{" "}
              <span className="muted">
                (configure your SMS gateway to POST inbound SMS here)
              </span>
            </label>
            <pre>{data.webhook_url}</pre>
          </>
        )}
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <div style={{ flex: "0 0 auto" }}>
            <button type="button" className="secondary" onClick={() => downloadSecrets(data)}>
              Download keys
            </button>{" "}
            <button onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SessionsModal({
  webapp,
  onClose,
}: {
  webapp: Webapp;
  onClose: () => void;
}) {
  const [items, setItems] = useState<OtpSession[] | null>(null);
  useEffect(() => {
    api.webappSessions(webapp.id).then((r) => setItems(r.items));
  }, [webapp.id]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ width: "min(900px, 96vw)" }} onClick={(e) => e.stopPropagation()}>
        <h3>{webapp.name} — last 100 sessions</h3>
        {items === null ? (
          <div className="muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="muted">No sessions yet.</div>
        ) : (
          <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Phone</th>
                <th>Status</th>
                <th>Created</th>
                <th>Resolved</th>
                <th>Callback</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id}>
                  <td data-label="Phone"><code>{s.phone}</code></td>
                  <td data-label="Status">
                    <span className={`pill ${pillClass(s.status)}`}>{s.status}</span>
                  </td>
                  <td data-label="Created">{fmtTime(s.created_at)}</td>
                  <td data-label="Resolved">{fmtTime(s.resolved_at)}</td>
                  <td data-label="Callback">
                    {s.callback_status ? (
                      <code>{s.callback_status}{s.callback_error ? ` ${s.callback_error}` : ""}</code>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <div style={{ flex: "0 0 auto" }}>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function pillClass(s: OtpSession["status"]): string {
  if (s === "verified") return "ok";
  if (s === "timeout" || s === "failed") return "bad";
  return "";
}

function billingLabel(w: Webapp): string {
  switch (w.billing_status) {
    case "unlimited":
      return "unlimited";
    case "subscribed":
      return "subscribed";
    case "trial_exhausted":
      return "trial used up";
    default:
      return "trial";
  }
}

function billingPill(s: Webapp["billing_status"]): string {
  if (s === "unlimited" || s === "subscribed") return "ok";
  if (s === "trial_exhausted") return "bad";
  return "";
}
